import React, { useEffect, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  RelationshipLine,
  calculateRelationshipLines,
  getStraightLinePath,
  getPolylinePathWithRoundedCorners,
  STEPPED_LINE_CORNER_RADIUS,
  LineCalculationOptions,
  getLineOptionsForViewport,
  SHORT_VIEWPORT_HEIGHT_PX,
  VERY_SHORT_VIEWPORT_HEIGHT_PX,
  isCompactTreeViewport
} from '@/utils/relationshipLines';
import { type HybridPedigreeStoreType } from '@/stores/HybridPedigreeStore';
import './relationship-lines.css';

interface RelationshipLinesProps {
  treeData: HybridPedigreeStoreType;
  containerRef: React.RefObject<HTMLDivElement | null>;
  options?: LineCalculationOptions;
}

const RelationshipLines: React.FC<RelationshipLinesProps> = observer(
  ({ treeData, containerRef, options: customOptions }) => {
    const [lines, setLines] = useState<RelationshipLine[]>([]);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const svgRef = useRef<SVGSVGElement>(null);
    const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : undefined;
    const options = getLineOptionsForViewport(windowHeight, windowWidth, customOptions);

    // Access observable properties during render so MobX tracks them
    // This ensures the component re-renders when tree structure changes
    const treeAnchorId = treeData.treeAnchorFamilyMemberId;
    const leftRootId = treeData.rootGen.leftRootFamilyMemberId;
    const rightRootId = treeData.rootGen.rightRootFamilyMemberId;
    const leftParentId = treeData.parentsGen.leftParentFamilyMemberId;
    const rightParentId = treeData.parentsGen.rightParentFamilyMemberId;

    // Create a key that changes when tree structure changes to force recalculation
    const treeStructureKey = `${treeAnchorId}-${leftRootId}-${rightRootId}-${leftParentId}-${rightParentId}`;

    const calculateLines = useCallback(() => {
      if (!containerRef.current) return;

      // Check if PersonCard elements are actually rendered
      const personCards = containerRef.current.querySelectorAll('[data-family-member-id]');
      if (personCards.length === 0) {
        return;
      }

      const newLines = calculateRelationshipLines(treeData, containerRef.current, options);
      setLines(newLines);

      // Update container size for SVG
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    }, [treeData, containerRef, options]);

    // Calculate lines on mount and when treeData changes
    useEffect(() => {
      // Add a small delay to ensure the layout has settled
      const timeoutId = setTimeout(calculateLines, 100);
      return () => clearTimeout(timeoutId);
    }, [calculateLines, treeStructureKey]);

    // Re-render when viewport height or width crosses threshold so options (line spacing) update
    const getViewportTier = () => {
      if (typeof window === 'undefined') return 0;
      const h = window.innerHeight;
      const w = window.innerWidth;
      if (isCompactTreeViewport(h, w)) return 3; /* compressed tree: short lines */
      if (h > SHORT_VIEWPORT_HEIGHT_PX) return 0;
      if (h <= VERY_SHORT_VIEWPORT_HEIGHT_PX) return 2;
      return 1;
    };
    const [, setViewportTier] = useState(getViewportTier);
    useEffect(() => {
      const check = () => {
        const tier = getViewportTier();
        setViewportTier((prev) => (tier !== prev ? tier : prev));
      };
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, []);

    // Handle window resize
    useEffect(() => {
      const handleResize = () => {
        // Debounce resize events
        const timeoutId = setTimeout(calculateLines, 100);
        return () => clearTimeout(timeoutId);
      };

      const timeoutId = setTimeout(handleResize, 100);
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
      };
    }, [calculateLines]);

    // Recalculate when container size changes (for responsive updates)
    useEffect(() => {
      if (!containerRef.current) return;

      const observer = new ResizeObserver(() => {
        calculateLines();
      });

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }, [calculateLines, containerRef]);

    if (lines.length === 0 || containerSize.width === 0) {
      return null;
    }

    return (
      <svg
        ref={svgRef}
        className="relationship-lines-svg"
        viewBox={`0 0 ${containerSize.width} ${containerSize.height}`}
        preserveAspectRatio="none"
      >
        {/* Render all relationship lines */}
        {lines.map((line) => {
          const pathData =
            line.points != null && line.points.length >= 2
              ? getPolylinePathWithRoundedCorners(line.points, STEPPED_LINE_CORNER_RADIUS)
              : getStraightLinePath(line.fromPoint, line.toPoint);

          return (
            <g key={line.id}>
              <path
                d={pathData}
                className={`relationship-line ${line.type === 'spouse' ? 'spouse-line' : 'parent-child-line'} ${line.isDashed ? 'dashed' : ''}`}
                data-line-id={line.id}
                data-from-family-member={line.fromFamilyMemberId}
                data-to-family-member={line.toFamilyMemberId}
              />
            </g>
          );
        })}
      </svg>
    );
  }
);

export default RelationshipLines;
